import {useId,useRef,useState} from 'react';
import {Permissions as P} from '@myfm/domain';
import {api} from '../api';
import {ConfirmationDialog} from '../ui/ConfirmationDialog';
import {useCapability} from '../ui/StaffSession';
import {withBuildingScope} from '../ui/building-scope';
import {ErrorState} from '../ui/State';
import './remove-to-recycle-bin.css';
import {otherReasonMinimum,removalReasons,removalRequest,type RemovableEntity,type RemovalReason} from '../ui/remove-to-recycle-bin';

type Props={entityType:RemovableEntity;entityId:string;name:string;building?:string|undefined;disabled?:boolean;onRemoved:(message:string)=>void};

/**
 * "Remove…" for an irrelevant image or document (owner decision, 11 Oct 2026). Document managers who may use the recycle bin
 * choose a reason; the item moves to the recycle bin (audited), leaves Media, registers and review queues, and its open
 * questions close. An administrator can restore it or approve its permanent removal from Administration > Recycle bin.
 */
export function RemoveToRecycleBin({entityType,entityId,name,building,disabled=false,onRemoved}:Props){
 const manage=useCapability(P.ManageDocuments),move=useCapability(P.MoveToRecycleBin),allowed=manage&&move;
 const [open,setOpen]=useState(false),[reason,setReason]=useState<RemovalReason|''>(''),[note,setNote]=useState('');
 const [error,setError]=useState<unknown>(),key=useRef(''),hint=useId();
 if(!allowed)return null;
 const request=removalRequest(entityType,entityId,reason,note,key.current);
 const start=()=>{key.current=crypto.randomUUID();setReason('');setNote('');setError(undefined);setOpen(true);};
 async function remove(){
  if(!request)return;setError(undefined);
  try{
   await api(withBuildingScope('/api/recycle-bin/move',building),{method:'POST',body:JSON.stringify(request)});
   setOpen(false);onRemoved(`${name} was removed to the recycle bin. An administrator can restore it.`);
  }catch(cause){setError(cause);}
 }
 return <>
  <button type="button" className="secondary" disabled={disabled} onClick={start}>Remove…</button>
  <ConfirmationDialog open={open} title={`Remove ${name}?`} tone="danger" confirmLabel="Remove" busyLabel="Removing…"
   confirmDisabled={!request} onConfirm={remove} onCancel={()=>setOpen(false)}
   description="It leaves Media, the document registers and review queues, and its open questions close. It is kept in the recycle bin, where an administrator can restore it or approve its permanent removal.">
   <fieldset className="remove-reasons"><legend>Why is it being removed?</legend>
    {removalReasons.map(option=><label key={option.value}><input type="radio" name={`${hint}-reason`} value={option.value}
     checked={reason===option.value} onChange={()=>setReason(option.value)}/> {option.label}</label>)}
   </fieldset>
   {reason==='Other'&&<><label>Say why<textarea rows={3} maxLength={1000} value={note} onChange={event=>setNote(event.target.value)}
    aria-describedby={hint}/></label><span id={hint}>At least {otherReasonMinimum} characters.</span></>}
   {reason&&reason!=='Other'&&<label>Note (optional)<textarea rows={2} maxLength={1000} value={note} onChange={event=>setNote(event.target.value)}/></label>}
   {Boolean(error)&&<ErrorState error={error} subject="removal"/>}
  </ConfirmationDialog>
 </>;
}
